<div class="lead-status-tracker-container" [class.is-updating]="isUpdating">
  <!-- Top Header: Title, Status Badge, Star Rating, and Other Statuses -->
  <div class="status-tracker-header">
    <span class="status-label">Lead Status</span>
    <!-- Current Status Badge — header right end -->
    <div class="current-status-badge" [ngClass]="currentStatusColorClass">
      <span class="status-pulse-dot"></span>
      <span class="status-text" [title]="currentStatusDisplay">{{
        currentStatusDisplay
      }}</span>
    </div>
  </div>

  <!-- Status Controls Row: Viability Rating | More Statuses -->
  <div class="status-controls-row">
    <!-- 5-Star Viability Rating (left) -->
    <div
      class="viability-rating-widget"
      [ngClass]="viabilityRatingColorClass"
      [title]="viabilityTooltip"
      (mouseleave)="clearHoverRating()"
    >
      <span class="rating-label-text">Viability</span>
      <div class="stars-group" role="group" aria-label="Viability Rating">
        @for (star of ratingStars; track star) {
          <button
            type="button"
            class="star-btn"
            [class.filled]="star <= (hoverRating || currentViabilityRating)"
            [class.hovered]="hoverRating >= star"
            [disabled]="!hasAccess || isSavingRating"
            (mouseenter)="setHoverRating(star)"
            (click)="onSelectRating(star)"
            [attr.aria-label]="
              star + ' stars - ' + (viabilityLabels[star] || '')
            "
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 576 512"
              width="11"
              height="11"
            >
              <path
                fill="currentColor"
                d="M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3l128.1-67.3 128.1 67.3c10.8 5.7 23.9 4.7 33.8-2.3s14.9-19.3 12.9-31.3L438.2 329 542.4 225.9c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381 150.3 316.9 18z"
              />
            </svg>
          </button>
        }
      </div>
    </div>

    <!-- Other Statuses Dropdown Button -->
    <div
      class="other-statuses-wrapper"
      (appClickOutside)="closeOtherStatuses()"
    >
      <button
        type="button"
        class="other-statuses-trigger"
        [class.active]="showOtherStatusesDropdown || isHoldingState"
        [disabled]="!hasAccess || isUpdating"
        (click)="toggleOtherStatuses()"
        aria-haspopup="true"
        [attr.aria-expanded]="showOtherStatusesDropdown"
      >
        <span class="trigger-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 512 512"
            width="11"
            height="11"
          >
            <path
              fill="currentColor"
              d="M40 48C26.7 48 16 58.7 16 72v48c0 13.3 10.7 24 24 24H88c13.3 0 24-10.7 24-24V72c0-13.3-10.7-24-24-24H40zM192 64c-17.7 0-32 14.3-32 32s14.3 32 32 32H480c17.7 0 32-14.3 32-32s-14.3-32-32-32H192zm0 160c-17.7 0-32 14.3-32 32s14.3 32 32 32H480c17.7 0 32-14.3 32-32s-14.3-32-32-32H192zm0 160c-17.7 0-32 14.3-32 32s14.3 32 32 32H480c17.7 0 32-14.3 32-32s-14.3-32-32-32H192zM16 232v48c0 13.3 10.7 24 24 24H88c13.3 0 24-10.7 24-24V232c0-13.3-10.7-24-24-24H40c-13.3 0-24 10.7-24 24zM40 368c-13.3 0-24 10.7-24 24v48c0 13.3 10.7 24 24 24H88c13.3 0 24-10.7 24-24V392c0-13.3-10.7-24-24-24H40z"
            />
          </svg>
        </span>
        <span class="trigger-text">More Statuses</span>
        <svg
          class="dropdown-arrow"
          [class.rotated]="showOtherStatusesDropdown"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
          width="9"
          height="9"
        >
          <path
            fill="currentColor"
            d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z"
          />
        </svg>
      </button>

      <!-- Dropdown Menu -->
      @if (showOtherStatusesDropdown) {
        <div class="other-statuses-dropdown" role="menu">
          <div class="dropdown-header">
            <span class="dropdown-title">More Status Options</span>
            <span class="dropdown-desc"
              >Select a paused, follow-up, or closed status for this lead</span
            >
          </div>
          <div class="dropdown-options">
            @for (option of otherStatuses; track option.value) {
              <button
                type="button"
                class="status-option-item"
                [class.selected]="option.value === normalizedCurrentStatus"
                [ngClass]="'color-' + option.colorClass"
                (click)="onSelectOtherStatus(option)"
                role="menuitem"
              >
                <div class="option-icon-box">
                  @switch (option.icon) {
                    @case ("info") {
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        width="13"
                        height="13"
                      >
                        <path
                          fill="currentColor"
                          d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336h24V272H216c-13.3 0-24-10.7-24-24s10.7-24 24-24h48c13.3 0 24 10.7 24 24v88h8c13.3 0 24 10.7 24 24s-10.7 24-24 24H216c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-208a32 32 0 1 1 0 64 32 32 0 1 1 0-64z"
                        />
                      </svg>
                    }
                    @case ("pause") {
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        width="13"
                        height="13"
                      >
                        <path
                          fill="currentColor"
                          d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM224 192V320c0 17.7-14.3 32-32 32s-32-14.3-32-32V192c0-17.7 14.3-32 32-32s32 14.3 32 32zm128 0V320c0 17.7-14.3 32-32 32s-32-14.3-32-32V192c0-17.7 14.3-32 32-32s32 14.3 32 32z"
                        />
                      </svg>
                    }
                    @case ("mail") {
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        width="13"
                        height="13"
                      >
                        <path
                          fill="currentColor"
                          d="M48 64C21.5 64 0 85.5 0 112c0 15.1 7.1 29.3 19.2 38.4L236.8 313.6c11.4 8.5 27 8.5 38.4 0L492.8 150.4c12.1-9.1 19.2-23.3 19.2-38.4c0-26.5-21.5-48-48-48H48zM0 176V384c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V176L294.4 339.2c-22.8 17.1-54 17.1-76.8 0L0 176z"
                        />
                      </svg>
                    }
                    @case ("minus") {
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        width="13"
                        height="13"
                      >
                        <path
                          fill="currentColor"
                          d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM184 232H328c13.3 0 24 10.7 24 24s-10.7 24-24 24H184c-13.3 0-24-10.7-24-24s10.7-24 24-24z"
                        />
                      </svg>
                    }
                    @case ("close") {
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        width="13"
                        height="13"
                      >
                        <path
                          fill="currentColor"
                          d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM175 175c9.4-9.4 24.6-9.4 33.9 0l47 47 47-47c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-47 47 47 47c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0l-47-47-47 47c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l47-47-47-47c-9.4-9.4-9.4-24.6 0-33.9z"
                        />
                      </svg>
                    }
                  }
                </div>
                <div class="option-details">
                  <div class="option-label-row">
                    <span class="option-label">{{ option.label }}</span>
                    @if (option.value === normalizedCurrentStatus) {
                      <span class="active-tag">Active</span>
                    }
                  </div>
                  <span class="option-desc">{{ option.description }}</span>
                </div>
              </button>
            }
          </div>
        </div>
      }
    </div>
  </div>

  <!-- Contextual Banner for Holding / Non-Linear Statuses -->
  @if (isHoldingState) {
    <div class="holding-state-banner">
      <div class="banner-icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
          width="14"
          height="14"
        >
          <path
            fill="currentColor"
            d="M256 32c14.2 0 27.3 7.5 34.5 19.8l216 368c7.3 12.4 7.3 27.7 .2 40.1S486.3 480 472 480H40c-14.3 0-27.6-7.7-34.7-20.1s-7-27.8 .2-40.1l216-368C228.7 39.5 241.8 32 256 32zm0 128c-13.3 0-24 10.7-24 24V296c0 13.3 10.7 24 24 24s24-10.7 24-24V184c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z"
          />
        </svg>
      </div>
      <div class="banner-content">
        <span class="banner-text">{{ holdingStateBannerText }}</span>
      </div>
    </div>
  }

  <!-- 3-Stage Stepper Track -->
  <div class="status-stepper-track" role="list">
    @for (
      stage of statusStages;
      track stage.step;
      let idx = $index;
      let last = $last
    ) {
      <div
        class="track-step-node"
        [class.completed]="stage.step < currentStatusStep"
        [class.active]="stage.step === currentStatusStep"
        [class.upcoming]="stage.step > currentStatusStep || isHoldingState"
        [class.terminal-stage]="stage.isTerminal"
        role="listitem"
      >
        <button
          type="button"
          class="stage-button"
          [disabled]="!hasAccess || isUpdating"
          (click)="onStageClick(stage)"
          [title]="
            stage.step === currentStatusStep
              ? 'Current Stage: ' + stage.name
              : 'Move to ' + stage.name
          "
        >
          <!-- Watery ripple hover background element -->
          <span class="watery-ripple-bg"></span>

          <!-- Step Indicator Icon / Badge -->
          <div class="step-indicator">
            @if (stage.step < currentStatusStep) {
              <!-- Completed Checkmark -->
              <span class="indicator-icon completed-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 448 512"
                  width="12"
                  height="12"
                >
                  <path
                    fill="currentColor"
                    d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"
                  />
                </svg>
              </span>
            } @else if (stage.step === currentStatusStep) {
              <!-- Active Glow Badge -->
              <span class="indicator-icon active-icon">
                <span class="active-pulse-ring"></span>
                <span class="step-num">{{ stage.step }}</span>
              </span>
            } @else {
              <!-- Upcoming Badge -->
              <span class="indicator-icon upcoming-icon">
                <span class="step-num">{{ stage.step }}</span>
              </span>
            }
          </div>

          <!-- Step Labels -->
          <div class="stage-text-block">
            <span class="stage-name">{{ stage.name }}</span>
            <span class="stage-sublabel">{{ stage.sublabel }}</span>
          </div>

          <!-- Active Caret Indicator -->
          @if (stage.step === currentStatusStep) {
            <span class="active-caret"></span>
          }
        </button>

        <!-- Connecting Bar to Next Step -->
        @if (!last) {
          <div
            class="track-connector"
            [class.filled]="stage.step < currentStatusStep"
            [class.active-path]="stage.step === currentStatusStep"
          >
            <span class="connector-line"></span>
          </div>
        }
      </div>
    }
  </div>

  <!-- Funded Amount Display -->
  @if (isFundedWithAmount) {
    <div class="funded-amount-banner">
      <span class="funded-amount-icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 448 512"
          width="11"
          height="11"
        >
          <path
            fill="currentColor"
            d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"
          />
        </svg>
      </span>
      <span class="funded-amount-label">Funded Amount</span>
      <span class="funded-amount-value">{{ formattedApprovedAmount }}</span>
    </div>
  }

  <!-- Status Notes Box -->
  <div
    class="status-notes-section"
    [class.is-dirty]="isNotesDirty"
    [class.is-empty]="!statusNotesInput"
  >
    <div class="notes-header-row">
      <div class="notes-title-group">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
          width="11"
          height="11"
          class="notes-icon"
        >
          <path
            fill="currentColor"
            d="M441 58.9L453.1 71c9.4 9.4 9.4 24.6 0 33.9L424 134.1 377.9 88 407 58.9c9.4-9.4 24.6-9.4 33.9 0zM209.8 256.2L344 121.9 390.1 168 255.8 302.2c-2.9 2.9-6.5 5-10.6 6.1l-52.6 14.3c-7.9 2.2-15.6-4.5-13.4-12.5l14.3-52.6c1.1-4 3.2-7.7 6.1-10.6zM64 64C28.7 64 0 92.7 0 128V448c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V288c0-17.7-14.3-32-32-32s-32 14.3-32 32V448H64V128H224c17.7 0 32-14.3 32-32s-14.3-32-32-32H64z"
          />
        </svg>
        <span class="notes-title">Notes</span>
      </div>

      <div class="notes-status-badge">
        @if (isNotesDirty) {
          <span class="unsaved-badge">
            <span class="unsaved-dot"></span>
            Unsaved
          </span>
          <button
            type="button"
            class="save-notes-btn"
            [disabled]="!hasAccess || isSavingNotes"
            (mousedown)="$event.preventDefault()"
            (click)="onSaveStatusNotes()"
            title="Save status note"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 448 512"
              width="10"
              height="10"
            >
              <path
                fill="currentColor"
                d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"
              />
            </svg>
            Save Note
          </button>
        } @else if (notesSavedRecently) {
          <span class="saved-badge">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 448 512"
              width="11"
              height="11"
            >
              <path
                fill="currentColor"
                d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"
              />
            </svg>
            Saved
          </span>
        } @else if (isSavingNotes) {
          <span class="saving-badge">Saving...</span>
        }
      </div>
    </div>

    <textarea
      #notesInput
      class="notes-input"
      placeholder="Add a status note or log a recent outreach update..."
      [(ngModel)]="statusNotesInput"
      [disabled]="!hasAccess || isSavingNotes"
      (keydown.enter)="$event.preventDefault(); onSaveStatusNotes()"
      (input)="autoResizeTextarea(notesInput)"
      rows="2"
    ></textarea>
  </div>

  <!-- Loading State Indicator Bar -->
  @if (isUpdating) {
    <div class="status-updating-bar">
      <div class="updating-progress"></div>
      <span class="updating-text">Updating lead status...</span>
    </div>
  }
</div>

<!-- Modal Dialog: Select Funded / TA Outcome -->
@if (showFundedDialog) {
  <app-popup
    [heading]="'Select Funded / TA Outcome'"
    [confirmText]="'Save'"
    [cancelText]="'Cancel'"
    [popupPosition]="'fixed'"
    (confirmClicked)="onConfirmFundedDialog()"
    (cancelClicked)="onCancelFundedDialog()"
  >
    <div class="funded-dialog-content">
      <p class="dialog-instruction">
        Select the successful outcome for this lead and provide the approved
        funding details.
      </p>

      <div class="funded-options-grid">
        <!-- Option 1: Active Funded (Loan) -->
        <label
          class="funded-card"
          [class.selected]="selectedFundedType === '9'"
        >
          <input
            type="radio"
            name="fundedType"
            value="9"
            [(ngModel)]="selectedFundedType"
          />
          <div class="card-body">
            <div class="card-icon loan-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 512 512"
                width="18"
                height="18"
              >
                <path
                  fill="currentColor"
                  d="M64 32C28.7 32 0 60.7 0 96v256c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V96c0-35.3-28.7-64-64-64H64zM224 224a32 32 0 1 1 64 0 32 32 0 1 1 -64 0zm-96 64c0 17.7-14.3 32-32 32s-32-14.3-32-32V192c0-17.7 14.3-32 32-32s32 14.3 32 32v96zm288 0c0 17.7-14.3 32-32 32s-32-14.3-32-32V192c0-17.7 14.3-32 32-32s32 14.3 32 32v96z"
                />
              </svg>
            </div>
            <div class="card-text">
              <span class="card-title">Active Funded (Loan)</span>
              <span class="card-desc"
                >Client secured debt financing or loan</span
              >
            </div>
          </div>
        </label>

        <!-- Option 2: Active Funded Grant -->
        <label
          class="funded-card"
          [class.selected]="selectedFundedType === '10'"
        >
          <input
            type="radio"
            name="fundedType"
            value="10"
            [(ngModel)]="selectedFundedType"
          />
          <div class="card-body">
            <div class="card-icon grant-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 512 512"
                width="18"
                height="18"
              >
                <path
                  fill="currentColor"
                  d="M176 56V96H336V56c0-13.3-10.7-24-24-24H200c-13.3 0-24 10.7-24 24zM128 96V56C128 25.1 153.1 0 184 0h144c30.9 0 56 25.1 56 56V96h32c35.3 0 64 28.7 64 64v96c0 35.3-28.7 64-64 64H384v160c0 17.7-14.3 32-32 32H160c-17.7 0-32-14.3-32-32V320H96c-35.3 0-64-28.7-64-64V160c0-35.3 28.7-64 64-64h32zM96 272h32V144H96c-8.8 0-16 7.2-16 16v96c0 8.8 7.2 16 16 16zm320 0c8.8 0 16-7.2 16-16V160c0-8.8-7.2-16-16-16H384V272h32z"
                />
              </svg>
            </div>
            <div class="card-text">
              <span class="card-title">Active Funded Grant</span>
              <span class="card-desc"
                >Client secured non-repayable grant capital</span
              >
            </div>
          </div>
        </label>

        <!-- Option 3: Technical Assistance -->
        <label
          class="funded-card"
          [class.selected]="selectedFundedType === '11'"
        >
          <input
            type="radio"
            name="fundedType"
            value="11"
            [(ngModel)]="selectedFundedType"
          />
          <div class="card-body">
            <div class="card-icon ta-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 512 512"
                width="18"
                height="18"
              >
                <path
                  fill="currentColor"
                  d="M256 0c4.6 0 9.2 1 13.4 2.9L457.7 82.8c22 9.3 38.4 31 38.3 57.2c-.5 99.2-41.3 280.7-213.6 363.2c-16.7 8-36.1 8-52.8 0C57.3 420.7 16.5 239.2 16 140c-.1-26.2 16.3-47.9 38.3-57.2L242.7 2.9C246.8 1 251.4 0 256 0zm0 66.8V444.8C394 378.1 431.1 230.1 432 141.4L256 66.8z"
                />
              </svg>
            </div>
            <div class="card-text">
              <span class="card-title">Technical Assistance</span>
              <span class="card-desc"
                >Business advisory or consulting service completed</span
              >
            </div>
          </div>
        </label>
      </div>

      <!-- Approved Loan/Grant Amount Field -->
      @if (selectedFundedType === "9" || selectedFundedType === "10") {
        <div class="amount-field-group">
          <label for="approvedAmountInput" class="field-label">
            Approved {{ selectedFundedType === "9" ? "Loan" : "Grant" }} Amount
            <span class="required">*</span>
          </label>
          <div
            class="amount-input-wrapper"
            [class.has-error]="!!approvedAmountError"
          >
            <span class="currency-prefix">$</span>
            <input
              id="approvedAmountInput"
              type="number"
              min="1"
              placeholder="e.g. 50000"
              [(ngModel)]="approvedAmountInput"
              class="amount-input"
            />
          </div>
          @if (approvedAmountError) {
            <span class="error-text">{{ approvedAmountError }}</span>
          }
        </div>
      }
    </div>
  </app-popup>
}

<!-- Modal Dialog: Downgrade / Branch Confirmation Warning -->
@if (showWarningDialog) {
  <app-popup
    [heading]="warningHeading"
    [confirmText]="'Proceed'"
    [cancelText]="'Cancel'"
    [popupPosition]="'fixed'"
    (confirmClicked)="onConfirmWarning()"
    (cancelClicked)="onCancelWarning()"
  >
    <div class="warning-dialog-content">
      <div class="warning-icon-box">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
          width="28"
          height="28"
        >
          <path
            fill="#f59e0b"
            d="M256 32c14.2 0 27.3 7.5 34.5 19.8l216 368c7.3 12.4 7.3 27.7 .2 40.1S486.3 480 472 480H40c-14.3 0-27.6-7.7-34.7-20.1s-7-27.8 .2-40.1l216-368C228.7 39.5 241.8 32 256 32zm0 128c-13.3 0-24 10.7-24 24V296c0 13.3 10.7 24 24 24s24-10.7 24-24V184c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z"
          />
        </svg>
      </div>
      <div class="warning-text-block">
        <p class="warning-main-text">{{ warningMessage }}</p>
        <span class="warning-sub-text"
          >You can advance or adjust this status again at any time.</span
        >
      </div>
    </div>
  </app-popup>
}
